import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import {
  fontWeightChoices,
  missingFontLabel,
  nearestWeight,
  normalizeFontWeight,
  primaryFontFamily,
  withCurrentFont,
} from 'shared/fonts';
import { FONT_FAMILY_OPTIONS } from './font-family-options';
import type { SelectOption } from './select-field';

interface FontField {
  value: string | undefined;
  isMixed: boolean;
  setValue: (next: string) => void;
}

/**
 * Связка «Шрифт» + «Начертание» инспекторов медиа-менеджера: пункты начертания — только веса
 * выбранного шрифта (реестр `shared/fonts`), смена шрифта переводит вес в ближайший настоящий.
 * Вес пишется числом в строке (`'700'`): значение читает и сервис рендера, а старые
 * `normal` / `bold` по-прежнему понимаются через `normalizeFontWeight`.
 */
export function useFontFields(fontFamily: FontField, fontWeight: FontField, afterChange?: () => void, fallbackFamily?: string) {
  const { t } = useLingui();
  const family = fontFamily.isMixed ? undefined : primaryFontFamily(fontFamily.value);

  const weightOptions: SelectOption[] = useMemo(
    () =>
      fontWeightChoices(family, fontWeight.isMixed ? undefined : fontWeight.value).map((choice) => ({
        value: String(choice.value),
        label: choice.label ? `${t(choice.label)} (${choice.value})` : String(choice.value),
      })),
    [family, fontWeight.isMixed, fontWeight.value, t],
  );

  // Шрифт старого шаблона вне набора (`Arial`) — отдельным пунктом: нативный select без него показал бы первый шрифт списка.
  const shownFamily = fontFamily.isMixed ? undefined : (fontFamily.value ?? fallbackFamily);
  const familyOptions: SelectOption[] = useMemo(
    () => (shownFamily ? withCurrentFont(FONT_FAMILY_OPTIONS, shownFamily, t(missingFontLabel(shownFamily))) : FONT_FAMILY_OPTIONS),
    [shownFamily, t],
  );

  const weightValue = fontWeight.value === undefined ? undefined : String(normalizeFontWeight(fontWeight.value, family));

  const changeFamily = (next: string) => {
    fontFamily.setValue(next);
    if (!fontWeight.isMixed && fontWeight.value !== undefined) {
      const nextFamily = primaryFontFamily(next);
      const weight = nearestWeight(nextFamily, fontWeight.value);
      if (weight !== normalizeFontWeight(fontWeight.value, nextFamily)) fontWeight.setValue(String(weight));
    }
    afterChange?.();
  };

  const changeWeight = (next: string) => {
    fontWeight.setValue(next);
    afterChange?.();
  };

  return { familyOptions, weightOptions, weightValue, changeFamily, changeWeight };
}
